import React, { useState } from 'react';
import Layout from './Layout';
import { motion } from 'framer-motion';
import { FaCopy } from 'react-icons/fa';


const AArticleSQLBeginner = () => {
  const [copiedStates, setCopiedStates] = useState({});

  const sections = [
    {
      id: 1,
      title: "Introduction to SQL for Finance Professionals",
      text: `Hi, I am Lucas, and as someone deeply engaged in the intersection of finance and technology, I want to introduce you to one of the most valuable tools for modern finance professionals: SQL (Structured Query Language). 

Whether you're a seasoned finance professional or someone just starting out in the field, mastering SQL can open new doors and elevate the way you analyze and manage data in your role. So, why should finance people care about SQL? In today's data-driven world, the ability to retrieve, analyze, and interpret large datasets is critical. SQL serves as the backbone for interacting with databases, enabling you to extract exactly the data you need to inform financial decisions, track performance, and uncover trends.

Think of it as your direct connection to the raw numbers that power the financial insights you generate. In my view, SQL's ability to bridge the gap between raw data and actionable insights makes it a must-have skill for finance professionals. You are no longer just relying on static reports or pre-defined dashboards; with SQL, you can ask specific questions and get tailored answers directly from the data. Whether you're analyzing revenue trends, identifying cost-saving opportunities, or validating complex financial models, SQL empowers you to work with data efficiently and accurately. 

What makes SQL particularly valuable for finance people is that it combines analytical rigor with strategic thinking. By mastering it, you can become the go-to person for data-driven insights in your organization. SQL allows you to work closely with data engineers, IT, and other technical teams while also providing value to leadership and decision-makers. It's a skill that enhances your ability to communicate effectively across functions, making you a more versatile and impactful finance professional.

Now that we've set the stage, let's dive into the core principles of SQL and how you can start applying it in your work. From querying databases to optimizing performance and generating insights, this tool will become an essential part of your financial toolkit.`,
      explanation: "In short, SQL serves as the backbone for interacting with databases, enabling you to extract exactly the data you need to inform financial decisions, track performance, and uncover trends."
    },
    {
      id: 2,
      title: "SQL in Finance",
      text: `There are three main areas where SQL can significantly enhance the work of finance professionals: data extraction and querying, financial analysis, and reporting and visualization. Let us now explore each of these in more depth.

**Data Extraction and Querying: Unlocking the Numbers**

At the heart of SQL lies its ability to retrieve and organize data efficiently. Think of SQL as the bridge between raw datasets and actionable financial insights. More formally, querying with SQL involves asking databases specific questions to pull out exactly the information you need. For example, a finance professional might use SQL to retrieve monthly revenue figures, identify overdue accounts, or calculate the average spending of key customers. 

The process typically begins with understanding the database structure—its tables, columns, and relationships. Once familiar with this structure, you can write queries to extract the necessary data. The ability to customize these queries allows you to tailor your analysis to the specific needs of your organization or project. As you know, financial data often resides in large and complex databases, which makes SQL's querying capabilities indispensable.

When data extraction with SQL is well executed, the results are transformative. Accurate and timely data retrieval enables better decision-making and reduces dependency on static reports.

For instance, well-structured SQL queries can identify trends in spending, highlight areas of inefficiency, or flag potential risks like late payments or declining profit margins. Moreover, SQL's flexibility allows finance professionals to ask "what if" questions and get answers quickly. This is especially useful during audits, budget reviews, or strategic planning sessions.

**Financial Analysis: Turning Data into Strategy**

Once the data has been extracted, SQL can be used to perform calculations and generate insights. This might involve calculating growth rates, performing variance analysis, or identifying patterns in revenue streams. Advanced SQL features such as window functions and subqueries can help you perform more sophisticated analyses, like ranking products by profitability or segmenting customers based on behavior. By leveraging these tools, finance professionals can uncover trends and deliver insights that drive strategic decisions.

**Reporting and Visualization: Bridging Communication Gaps**

SQL can be integrated with visualization tools like Tableau or Power BI to create dynamic dashboards. These dashboards provide clear and actionable visuals, making it easier for stakeholders to understand the story behind the numbers. 

A strong SQL foundation also allows finance professionals to prepare and automate recurring reports, saving time and ensuring consistency. Below an example of a SQL query that calculates total revenue per month for a given year and with this information, you can create a dynamic revenue trend chart in a visualization tool like Power BI or Tableau that is automated and updated in real-time when you have your data source connected.`,
      code: `SELECT 
  SUM(amount) as total_revenue,
  MONTH(transaction_date) as month
FROM financial_transactions
WHERE YEAR(transaction_date) = 2024
GROUP BY MONTH(transaction_date)
ORDER BY month;`,
      explanation: "This query demonstrates how to analyze monthly revenue trends by calculating total revenue per month, showcasing SQL's ability to aggregate and organize financial data effectively."
    },
    {
      id: 3,
      title: "SQL and the ERP System",
      text: `In today's interconnected business world, ERP (Enterprise Resource Planning) systems are the backbone of financial and operational data. These systems consolidate information from various departments—finance, procurement, sales, HR, and more—into a central database.

For finance professionals, understanding how SQL interacts with an ERP system is critical to unlocking the full potential of these tools. 

SQL acts as the key that allows you to access, interpret, and utilize the wealth of data stored within an ERP system. This chapter explores how SQL integrates with ERP systems, why it's important, and how finance professionals can use SQL to enhance their role.

**What is an ERP System, and Why Does SQL Matter**

An ERP system is a comprehensive software solution that centralizes a company's data and streamlines business processes. Popular ERP systems like SAP, Oracle NetSuite, or Microsoft Dynamics store vast amounts of structured data, from financial transactions to inventory levels and employee records.

SQL becomes invaluable here because it allows you to interact directly with the ERP database. Instead of relying solely on pre-defined reports or dashboards, SQL lets you extract specific, customized datasets that are often unavailable through the system's built-in tools.

For instance, with SQL, you can:
- Identify overdue invoices for specific clients
- Compare actual sales against forecasts for specific product categories
- Generate ad-hoc reports that combine data across multiple modules, such as linking sales data with inventory levels

How SQL Interacts with ERP Systems

ERP systems are built on relational databases, and SQL is the language used to query these databases. The data within an ERP system is typically organized into tables, each representing a different aspect of the business, such as customers, orders, or payments. Understanding the relationships between these tables is critical.

For example:
- Sales Data: A sales transaction might be linked to tables for customers, products, and inventory
- Accounts Payable: An invoice might connect to tables for suppliers, purchase orders, and payments

Using SQL, finance professionals can write queries to join these tables and uncover insights that aren't immediately visible.`,
      code: `SELECT 
  c.customer_name,
  COUNT(i.invoice_id) as invoice_count,
  SUM(i.amount) as total_amount,
  MAX(i.due_date) as latest_due_date
FROM customers c
JOIN invoices i ON c.customer_id = i.customer_id
WHERE i.status = 'overdue'
GROUP BY c.customer_name
HAVING COUNT(i.invoice_id) > 1
ORDER BY total_amount DESC;`,
      explanation: "This query demonstrates how to analyze overdue invoices by customer, showing the power of SQL in extracting meaningful insights from ERP data. It identifies customers with multiple overdue invoices and their total outstanding amounts."
    },
    {
      id: 4,
      title: "Essential SQL Queries for Finance Professionals",
      text: `**The Basics**

At the heart of SQL lies the SELECT statement, which is used to retrieve data from a database. For finance professionals, mastering this and its associated clauses is essential.

Example Use Case: Suppose you need a list of all open invoices from an accounts receivable table, the query could look like this:

SELECT specifies the columns you want to retrieve (e.g., invoice ID, customer name, due date and amount due).
FROM tells SQL which table to query (in above example an accounts_receivable table) and WHERE filters the data to show only open invoices.

**Aggregations and Calculations: Summarizing Data**

Financial analysis often involves summarizing data in totals, averages, maximums, and minimums. SQL makes this straightforward with aggregate functions.

Below an example query of total revenue from all sales for the current quarter:

In this query:
- SUM(amount) adds up all the values in the amount column to calculate total revenue
- AS total_revenue renames the result for clarity in the output
- BETWEEN filters the data to include only sales within the specified date range, here Q4

Other useful aggregations are COUNT, AVG, MIN, and MAX.

**Advanced Joins: Combining Data Across Tables**

ERP systems store related data in different tables. To create meaningful insights, you often need to combine data from multiple tables. This is where joins come in which are very useful especially when utilized with keys.`,
      code: `-- Basic SELECT query for open invoices
SELECT invoice_id, customer_name, due_date, amount_due 
FROM accounts_receivable 
WHERE status = 'Open';

-- Aggregation query for quarterly revenue
SELECT SUM(amount) AS total_revenue  
FROM sales  
WHERE sale_date 
BETWEEN '2024-01-01' AND '2024-03-31';

-- Advanced join for customer analysis
SELECT c.customer_name, c.contact_email, 
       ar.invoice_id, ar.amount_due 
FROM customers c 
INNER JOIN accounts_receivable ar 
ON c.customer_id = ar.customer_id 
WHERE ar.status = 'Open';`,
      explanation: "These queries demonstrate essential SQL operations for finance professionals: basic data retrieval, aggregations for analysis, and combining data from multiple tables. Each example addresses common financial tasks like tracking invoices, calculating revenue, and analyzing customer data."
    },
    {
      id: 5,
      title: "Optimizing Query Performance",
      text: `As you start to work with increasingly complex datasets and queries, performance starts becoming important. Poorly optimized SQL queries can lead to long processing times, system slowdowns, and even incorrect results if the database times out or fails to process data effectively. In this chapter, we'll explore practical techniques to optimize your SQL queries for performance, ensuring that your analysis remains efficient, accurate, and impactful.

**Why Query Optimization Matters**

As ERP systems often contain massive datasets spanning multiple years and departments. Without optimization, queries can become resource-intensive, affecting not only your work but also the performance of the entire system. 

For finance professionals, optimization ensures primary 3 things:
- Faster insights for real-time decision-making
- Reduced dependency on IT teams for troubleshooting
- Accurate results without unnecessary strain on the database

**7 Key Optimization Strategies**

1. Utilizing Indexes: Like in a book, indexes allows the database to quickly locate specific information (rows) in a table.

2. Limiting Data Queried: Only retrieve the columns and rows you actually need.

3. Optimizing Joins: Use indexed columns for joining and filter before joining.

4. Efficient Aggregation: Group data efficiently and pre-filter before aggregating.

5. Leverage Query Execution Plans: Use database tools to analyze query performance.

6. Simplify Complex Queries: Break down complex queries into manageable parts.

7. Avoid Unnecessary Subqueries: Use joins instead when possible.`,
      code: `-- Example of optimized vs unoptimized queries
-- Unoptimized: Selecting all columns
SELECT * 
FROM financial_transactions 
WHERE transaction_date > '2024-01-01';

-- Optimized: Selecting specific columns with index
SELECT transaction_id, amount, transaction_date
FROM financial_transactions 
WHERE transaction_date > '2024-01-01'
  AND amount > 1000
LIMIT 1000;

-- Optimized join with pre-filtering
SELECT 
  c.customer_name,
  SUM(t.amount) as total_spend
FROM customers c
INNER JOIN (
  SELECT customer_id, amount 
  FROM transactions 
  WHERE transaction_date >= '2024-01-01'
) t ON c.customer_id = t.customer_id
GROUP BY c.customer_name;`,
      explanation: "These examples demonstrate key optimization techniques: selecting specific columns, using appropriate filters, and optimizing joins. The optimized queries will execute faster and use fewer system resources."
    },
    {
      id: 6,
      title: "Recap",
      text: `This guide introduces SQL as an essential tool for finance professionals, offering insights into how it empowers them to unlock the potential of financial data within ERP systems.

**Chapter 1: Introduction**
We began by highlighting the relevance of SQL for finance professionals in managing, analyzing, and extracting actionable insights from financial data. SQL bridges the gap between raw data in ERP systems and decision-making, enabling finance teams to become more data-driven and efficient.

**Chapter 2: SQL in Finance**
The fundamentals of SQL were covered, focusing on key commands like SELECT, WHERE, GROUP BY, and JOIN. Practical examples demonstrated how these commands help finance professionals answer common business questions, such as tracking expenses, analyzing revenue trends, and reconciling accounts.

**Chapter 3: SQL and the ERP System**
This chapter explored how SQL interacts with ERP systems, emphasizing the structure of ERP databases. We explained how to locate financial tables, understand relationships between datasets, and query ERP data effectively. Key ERP-specific considerations, such as handling large datasets and respecting database schema, were also discussed.

**Chapter 4: Essential SQL Queries for Finance Professionals**
On the basics, this chapter delved into practical applications of SQL. We demonstrated how to use queries to generate income statements, track overdue invoices, and calculate KPIs like gross margin and ROI. The focus was on turning financial questions into SQL commands that provide actionable insights.

**Chapter 5: Optimizing Query Performance**
Performance optimization is crucial when working with large datasets in ERP systems. Techniques like indexing, filtering data efficiently, optimizing joins, and using execution plans were discussed. The goal is to ensure queries run faster and deliver accurate results, reducing system strain and improving decision-making agility.

With these foundational skills, finance professionals are better equipped to handle data-driven challenges. Advanced techniques, covered in subsequent chapters, further elevate their analytical capabilities.`,
      explanation: "This recap summarizes the key concepts and practical applications of SQL in finance, providing a comprehensive overview of the skills covered in this guide."
    }
  ];

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedStates({ ...copiedStates, [id]: true });
    setTimeout(() => {
      setCopiedStates(prev => ({ ...prev, [id]: false }));
    }, 2000);
  };

  // Update the scrollToSection function to account for header height
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100; // Adjust this value based on your header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  // Add this function at the top of your component, after the sections array
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return (
    <div>
      <Layout />
      <div className="w-full min-h-screen bg-gradient-to-b from-white to-white text-black pt-20">
        <div className="max-w-screen-lg mx-auto p-4">
          <div className="mb-12 flex justify-center">
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm max-w-[600px] w-full">
              <h2 className="text-2xl font-bold mb-4 text-center">Table of Contents</h2>
              <div className="space-y-2">
                {sections.map(section => section.title && (
                  <div 
                    key={section.id} 
                    className="cursor-pointer hover:scale-110 transition-colors text-center"
                    
                    onClick={() => scrollToSection(`section-${section.id}`)}
                  >
                    {section.id}. {section.title}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="space-y-16">
            {sections.map(({ id, title, text, code, explanation }) => (
              <motion.div 
                key={id}
                id={`section-${id}`}  // Add this line
                initial={{ opacity: 1, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1 }}
                className="flex flex-col gap-6"
              >
                <h2 className="text-3xl font-bold">{title}</h2>
                <div className="text-lg whitespace-pre-wrap">{text}</div>
                
                {code && (
                  <div className="relative">
                    <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto">
                      <code className="text-sm">{code}</code>
                    </pre>
                    <button
                      onClick={() => copyToClipboard(code, id)}
                      className="absolute top-2 right-2 p-2 bg-white rounded-md hover:bg-gray-200"
                    >
                      <FaCopy />
                      {copiedStates[id] && (
                        <span className="absolute -right-16 top-2 text-green-600">
                          Copied!
                        </span>
                      )}
                    </button>
                  </div>
                )}
                
                {explanation && (
                  <p className="text-gray-600 italic">{explanation}</p>
                )}
              </motion.div>
            ))}
          </div>
          {/* Back to Top Button */}
          <div className="flex justify-center mt-12 mb-8">
            <button
              onClick={scrollToTop}
              className=" text-white font-semibold py-2 px-4 rounded-lg shadow transition-colors duration-300 hover:scale-110" 
              style={{ background: 'rgb(1, 50, 32)' } }>
              Back to Top
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AArticleSQLBeginner;